import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtBRL, fmtNumber, fmtPct } from "../lib/format";
import { useFilters, periodToDates } from "../lib/filters";
import { KpiTile } from "../components/intelligence/KpiTile";
import { CardMemoryTrigger } from "../components/card-memory/CardMemoryTrigger";
import { PageHeader } from "../components/PageHeader";
import { IconShield, IconAlertTriangle, IconInfo, IconChevronRight, IconX } from "../components/icons";
import { LoadMoreRow, useProgressiveList } from "../components/LoadMoreRow";
import type { TransportInsuranceDto } from "@bi/types";
import { QueryErrorBanner } from "../components/QueryErrorBanner";

/** taxa já vem em escala percentual (0,30 = 0,30%). 3 casas p/ taxas pequenas. */
const pctRate = (v: number | null) => (v == null ? "—" : fmtPct(v, 3));
const pct1 = (v: number) => `${v.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

/** "0,03" / "0.03" → 0.03; vazio ou inválido → null. */
function parseRate(s: string): number | null {
  const n = Number(s.trim().replace(",", "."));
  return s.trim() !== "" && Number.isFinite(n) && n > 0 && n < 100 ? n : null;
}

export function InsurancePage() {
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  // Simulação de taxa: só muda o cálculo exibido (?rate= na API), não grava
  // nada. Útil enquanto a apólice real não está cadastrada.
  const [rateInput, setRateInput] = useState("");
  const [simRate, setSimRate] = useState<number | null>(null);

  const { data, isLoading, isError: dataError, refetch: dataRefetch, isFetching } = useQuery({
    queryKey: ["transport", "insurance", from, to, simRate],
    queryFn: () =>
      api.get<TransportInsuranceDto>(
        `/dashboards/transport/insurance?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${simRate != null ? `&rate=${simRate}` : ""}`,
      ),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  const byPlate = data?.byPlate ?? [];
  const plateList = useProgressiveList(byPlate);
  const goodsTotal = data?.goodsValueTotal ?? 0;
  const cteTotal = byPlate.reduce((s, p) => s + p.cteCount, 0);
  const maxGoods = Math.max(1, ...byPlate.map((p) => p.goodsValue));
  const top10Share = goodsTotal > 0 ? (byPlate.slice(0, 10).reduce((s, p) => s + p.goodsValue, 0) / goodsTotal) * 100 : 0;
  const hasRate = data?.ratePercent != null;
  const simulando = data?.rateSource === "override";
  const semPagamento = data != null && data.actualCargoPaid === 0;

  const aplicarSimulacao = () => setSimRate(parseRate(rateInput));
  const limparSimulacao = () => { setRateInput(""); setSimRate(null); };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Seguro de Carga"
        subtitle="Exposição = valor da mercadoria × taxa da apólice, comparada ao seguro pago"
      />

      {dataError && <QueryErrorBanner onRetry={() => { dataRefetch(); }} />}

      {/* Situação da configuração + simulador. Antes eram só avisos soltos
          ("configure a taxa") sem dizer onde nem permitir ver o efeito. */}
      {data && (data.rateSource !== "policy" || data.isReference || semPagamento) && (
        <div className="card overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-surface-border">
            <div className="p-5 space-y-3">
              <p className="text-sm font-semibold text-gray-900">O que falta para esta tela ficar completa</p>
              {!data.hasPolicy && (
                <div className="flex gap-2.5 rounded-lg bg-warning-50 border border-warning-100 px-3 py-2.5">
                  <IconAlertTriangle className="w-4 h-4 text-warning-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-warning-700 leading-snug">
                    <strong>Nenhuma apólice de carga cadastrada.</strong> Sem a taxa (%) da apólice, o seguro estimado
                    não pode ser calculado. Enquanto isso, use a simulação ao lado.
                  </p>
                </div>
              )}
              {data.isReference && (
                <div className="flex gap-2.5 rounded-lg bg-warning-50 border border-warning-100 px-3 py-2.5">
                  <IconAlertTriangle className="w-4 h-4 text-warning-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-warning-700 leading-snug">
                    A taxa de <strong>{pctRate(data.ratePercent)}</strong> é um valor de <strong>referência</strong>
                    {" "}({data.policyName}). Confirme o percentual real das apólices com o cliente.
                  </p>
                </div>
              )}
              {semPagamento && (
                <div className="flex gap-2.5 rounded-lg bg-surface-muted border border-surface-border px-3 py-2.5">
                  <IconInfo className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-600 leading-snug">
                    <strong>Nenhum pagamento de seguro de carga encontrado no período.</strong> O seguro pago vem das contas
                    financeiras classificadas como seguro. Se a empresa paga seguro, a conta provavelmente está sem esse tipo
                    no plano de contas (o mesmo motivo de "Outros" em <Link to="/frota/custo-placa" className="text-tm-700 hover:underline">Custo por Placa</Link>).
                  </p>
                </div>
              )}
            </div>

            <div className="p-5">
              <p className="text-sm font-semibold text-gray-900">Simular uma taxa</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Veja o seguro estimado com uma taxa hipotética. Só muda o que aparece nesta tela, nada é gravado.
              </p>
              <form className="mt-3 flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); aplicarSimulacao(); }}>
                <div className="relative w-36">
                  <input
                    inputMode="decimal"
                    value={rateInput}
                    onChange={(e) => setRateInput(e.target.value)}
                    placeholder="ex.: 0,03"
                    aria-label="Taxa da apólice em porcentagem"
                    className="w-full rounded-lg border border-surface-border px-3 py-2 pr-7 text-sm focus:outline-none focus:ring-2 focus:ring-tm-500"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">%</span>
                </div>
                <button type="submit" className="btn-primary" disabled={parseRate(rateInput) == null}>Simular</button>
                {simulando && (
                  <button type="button" className="btn-secondary inline-flex items-center gap-1" onClick={limparSimulacao}>
                    <IconX className="w-3.5 h-3.5" /> Limpar
                  </button>
                )}
              </form>
              {rateInput.trim() !== "" && parseRate(rateInput) == null && (
                <p className="mt-1.5 text-xs text-danger-600">Digite uma taxa entre 0 e 100, por exemplo 0,03.</p>
              )}
              {simulando && (
                <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-info-50 px-2.5 py-1 text-xs font-medium text-info-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-info-500" />
                  Simulando com {pctRate(data.ratePercent)}{isFetching ? " · calculando…" : ""}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <CardMemoryTrigger memory={{ cardKey: "seguro.mercadoria", from, to, expected: data?.goodsValueTotal, title: "Valor da Mercadoria" }}>
          <KpiTile label="Valor da mercadoria" value={data ? data.goodsValueTotalFormatted : "—"}
            footnote={data ? `Carga transportada em ${fmtNumber(cteTotal)} CT-es · ${fmtNumber(byPlate.length)} placas` : undefined}
            loading={isLoading} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "seguro.taxa", from, to, expected: data?.ratePercent, title: "Taxa Aplicada" }}>
          <KpiTile label="Taxa aplicada" value={data ? pctRate(data.ratePercent) : "—"}
            footnote={data ? (data.rateSource === "policy" ? (data.isReference ? "Apólice (valor de referência)" : "Da apólice cadastrada")
              : data.rateSource === "override" ? "Simulação (não gravada)" : "Sem apólice cadastrada") : undefined}
            tone={simulando ? null : data && !hasRate ? "warning" : null}
            loading={isLoading} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "seguro.estimado", from, to, expected: data?.estimatedInsurance, title: "Seguro Estimado" }}>
          <KpiTile label={simulando ? "Seguro estimado (simulado)" : "Seguro estimado"}
            value={data?.estimatedInsuranceFormatted ?? "—"}
            footnote={hasRate ? "Mercadoria × taxa" : "Precisa da taxa da apólice"} loading={isLoading} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "seguro.pago", from, to, expected: data?.actualCargoPaid, title: "Seguro Pago (real)" }}>
          <KpiTile label="Seguro pago (real)" value={data ? data.actualCargoPaidFormatted : "—"}
            footnote={semPagamento ? "Nenhum pagamento classificado como seguro" : "RCTR-C / carga, pelo financeiro"}
            tone={semPagamento ? "warning" : null} loading={isLoading} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "seguro.taxa-efetiva", from, to, expected: data?.effectiveRatePercent, title: "Taxa Efetiva Paga" }}>
          <KpiTile label="Taxa efetiva paga" value={data && !semPagamento ? pctRate(data.effectiveRatePercent) : "—"}
            // Sem pagamento classificado, o "gap" seria o estimado inteiro e
            // pareceria seguro não pago, quando o mais provável é conta sem tipo.
            footnote={data?.gap != null && data.gapFormatted && !semPagamento
              ?`${data.gap > 0 ? "Pago abaixo do estimado em" : "Pago acima do estimado em"} ${data.gapFormatted}`
              : "Seguro pago ÷ valor da mercadoria"}
            status={data && data.gap != null && data.gap > 0 && !semPagamento ? "warning" : null}
            loading={isLoading} />
        </CardMemoryTrigger>
      </div>

      {/* Ranking por placa */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border flex items-start gap-2">
          <IconShield className="w-4 h-4 text-gray-400 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-gray-900">Exposição por veículo</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Valor de mercadoria transportada por placa
              {byPlate.length > 10 ? ` · as 10 maiores levam ${pct1(top10Share)} do total` : ""}
            </p>
          </div>
        </div>
        {isLoading ? (
          <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
        ) : byPlate.length === 0 ? (
          <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem carga no período.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 border-b border-surface-border">
                  <th className="pl-5 pr-2 py-2 font-medium w-10">#</th>
                  <th className="px-2 py-2 font-medium">Placa</th>
                  <th className="px-2 py-2 font-medium text-right">CT-es</th>
                  <th className="px-2 py-2 font-medium text-right">Média por CT-e</th>
                  <th className="px-2 py-2 font-medium w-[28%]">Participação</th>
                  <th className="px-2 py-2 font-medium text-right">Valor da mercadoria</th>
                  <th className={`pl-2 pr-5 py-2 font-medium text-right ${hasRate ? "" : "text-gray-300"}`}>
                    {simulando ? "Seguro (simulado)" : "Seguro estimado"}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {plateList.visible.map((p, i) => {
                  const share = goodsTotal > 0 ? (p.goodsValue / goodsTotal) * 100 : 0;
                  return (
                    <tr key={p.plate} className="text-gray-700 hover:bg-surface-muted/50">
                      <td className="pl-5 pr-2 py-2.5 text-xs font-bold text-gray-400">{i + 1}</td>
                      <td className="px-2 py-2.5">
                        <Link to={`/frota/placa/${encodeURIComponent(p.plate)}`}
                          className="inline-flex items-center gap-1 font-medium text-tm-700 hover:underline whitespace-nowrap">
                          {p.plate} <IconChevronRight className="w-3 h-3 opacity-60" />
                        </Link>
                      </td>
                      <td className="px-2 py-2.5 text-right">{fmtNumber(p.cteCount)}</td>
                      <td className="px-2 py-2.5 text-right text-gray-500 whitespace-nowrap">
                        {p.cteCount > 0 ? fmtBRL(p.goodsValue / p.cteCount) : "—"}
                      </td>
                      <td className="px-2 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full bg-surface-muted overflow-hidden">
                            <div className="h-full rounded-full bg-info-500/70" style={{ width: `${(p.goodsValue / maxGoods) * 100}%` }} />
                          </div>
                          <span className="w-12 text-right text-xs text-gray-500">{pct1(share)}</span>
                        </div>
                      </td>
                      <td className="px-2 py-2.5 text-right font-semibold text-gray-900 whitespace-nowrap">{p.goodsValueFormatted}</td>
                      <td className={`pl-2 pr-5 py-2.5 text-right whitespace-nowrap ${p.estimatedFormatted ? (simulando ? "text-info-700" : "text-gray-700") : "text-gray-300"}`}>
                        {p.estimatedFormatted ?? "—"}
                      </td>
                    </tr>
                  );
                })}
                {/* capped=false: a lista vem inteira da API (não é o teto de ranking). */}
                <LoadMoreRow list={plateList} colSpan={7} label="placas" capped={false} />
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
